'use client';

import React, { useEffect, useRef } from 'react';
import { SHOPIFY_CONFIG } from '@/data/store';

interface ProductImageProps {
  buyButtonId: string;
  id: string;
}

declare global {
  interface Window {
    ShopifyBuy?: any;
  }
}

const ProductImage: React.FC<ProductImageProps> = ({ buyButtonId, id }) => {
  const prevBuyButtonIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (buyButtonId && prevBuyButtonIdRef.current?.toString() !== buyButtonId?.toString()) {
      prevBuyButtonIdRef.current = buyButtonId;

      const scriptURL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';

      function ShopifyBuyInit() {
        if (!window.ShopifyBuy) return;

        const client = window.ShopifyBuy.buildClient({
          domain: SHOPIFY_CONFIG.domain,
          apiKey: SHOPIFY_CONFIG.apiKey,
          appId: SHOPIFY_CONFIG.appId,
        });

        window.ShopifyBuy.UI.onReady(client).then(function (ui: any) {
          const node = document.getElementById(`product-image-component-${id}`);
          if (!node) return;

          ui.createComponent('product', {
            id: buyButtonId,
            node: node,
            moneyFormat: '%24%7B%7Bamount%7D%7D',
            options: {
              product: {
                variantId: 'all',
                width: '100%',
                contents: {
                  img: false,
                  imgWithCarousel: true,
                  title: false,
                  variantTitle: false,
                  price: false,
                  description: false,
                  buttonWithQuantity: false,
                  quantity: false,
                  button: false,
                  options: false,
                },
                styles: {
                  product: {
                    'text-align': 'center',
                    '@media (min-width: 601px)': {
                      'max-width': '100%',
                      'margin-left': '0',
                      'margin-bottom': '0px',
                    },
                  },
                  img: {
                    'max-width': '100%',
                    'max-height': '500px',
                    'width': 'auto',
                    'height': 'auto',
                    'object-fit': 'contain',
                    'margin': '0 auto',
                    'display': 'block',
                    'border-radius': '8px',
                    'background': '#fafafa',
                    '@media (max-width: 767px)': {
                      'max-height': '350px',
                    },
                  },
                  imgWrapper: {
                    'background': '#fafafa',
                    'border-radius': '12px',
                    'padding': '20px',
                    'min-height': '400px',
                    'display': 'flex',
                    'align-items': 'center',
                    'justify-content': 'center',
                    '@media (max-width: 767px)': {
                      'min-height': '300px',
                      'padding': '16px',
                    },
                  },
                  carousel: {
                    'display': 'flex',
                    'gap': '8px',
                    'margin-top': '16px',
                    'justify-content': 'center',
                    'flex-wrap': 'wrap',
                  },
                  carouselItem: {
                    'width': '60px',
                    'height': '60px',
                    'border': '2px solid #e8e8e8',
                    'border-radius': '6px',
                    'cursor': 'pointer',
                    'transition': 'all 0.2s ease',
                    'overflow': 'hidden',
                    ':hover': {
                      'border-color': '#00ad23',
                    },
                  },
                  carouselItemSelected: {
                    'border-color': '#00ad23',
                    'box-shadow': '0 0 0 2px rgba(0, 173, 35, 0.2)',
                  },
                  button: {
                    display: 'none',
                  },
                  option: {
                    display: 'none !important',
                  },
                  selectWrapper: {
                    display: 'none !important',
                  },
                  select: {
                    display: 'none !important',
                  },
                },
                googleFonts: ['Lato'],
              },
              cart: {
                contents: {
                  button: true,
                },
                styles: {
                  button: {
                    'font-family': 'Lato, sans-serif',
                    'font-size': '13px',
                    'padding-top': '14.5px',
                    'padding-bottom': '14.5px',
                    'font-weight': 'bold',
                    ':hover': {
                      'background-color': '#6ca44e',
                    },
                    ':focus': {
                      'background-color': '#00ad23',
                    },
                  },
                },
                googleFonts: ['Lato'],
              },
              modalProduct: {
                contents: {
                  img: false,
                  imgWithCarousel: false,
                  variantTitle: false,
                  buttonWithQuantity: false,
                  button: false,
                  quantity: false,
                },
                styles: {
                  product: {
                    'display': 'none',
                    '@media (min-width: 601px)': {
                      'max-width': '100%',
                      'margin-left': '0px',
                      'margin-bottom': '0px',
                    },
                  },
                },
                googleFonts: ['Lato'],
              },
              toggle: {
                styles: {
                  toggle: {
                    'font-family': 'Lato, sans-serif',
                    'font-weight': 'bold',
                    ':hover': {
                      'background-color': '#6ca44e',
                    },
                    ':focus': {
                      'background-color': '#6ca44e',
                    },
                  },
                },
                googleFonts: ['Lato'],
              },
            },
          });
        });
      }

      function loadScript() {
        const script = document.createElement('script');
        script.async = true;
        script.src = scriptURL;
        (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(script);
        script.onload = ShopifyBuyInit;
      }

      if (window.ShopifyBuy) {
        if (window.ShopifyBuy.UI) {
          ShopifyBuyInit();
        } else {
          loadScript();
        }
      } else {
        loadScript();
      }
    }
  }, [buyButtonId, id]);

  return (
    <div style={{
      width: '100%',
      margin: '0',
      minHeight: '300px'
    }}>
      <div id={`product-image-component-${id}`} style={{
        margin: 'auto',
        width: '100%',
        minHeight: '300px'
      }} />
    </div>
  );
};

export default ProductImage;
